import {useCallback, useEffect, useMemo, useState} from "react";
import {MaterialReactTable, useMaterialReactTable} from "material-react-table";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CancelIcon from '@mui/icons-material/Cancel';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import {useSnackbar} from "notistack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {Link} from "react-router";
import Stack from "@mui/material/Stack";
import {damsFetch} from "../app/damsFetch";
import {clientLog} from "../clientLog";
import {getHrDateAndTime} from "../utility";
import {useDocumentTranslation} from "../documents/documentContext";
import {getTableTranslations} from "../table-base/getTableTranslations";
import {deliveryStatusText} from "./shareDelivery";
import {visuallyHidden} from "@mui/utils";

/**
 * Panel for viewing, deactivating, and renewing public shares of a document.
 *
 * @param {Object}  model       - The document model ({ uniqueId }).
 * @param {boolean} open        - True when the parent dialog is open.
 * @param {number}  refreshKey  - Increment to trigger a data reload (e.g. after a new share).
 */
export const ShareObjectHistoryPanel = ({model, open, refreshKey}) => {
    const t = useDocumentTranslation();
    const {enqueueSnackbar} = useSnackbar();

    const [data, setData] = useState([]);

    const _getRowSharedUniqueId = row => row.original['sharedUniqueId'];

    const _canChange = row => row.original['canChange'] === true;

    const _isForbidden = error => error === 403;

    const _execPost = async (endpoint, sharedUniqueId, successMessage, errorMessage) => {
        try {
            await damsFetch(`/share/${endpoint}/`, {
                'correlation-id': crypto.randomUUID(),
                method: 'POST',
                body: JSON.stringify({'shared_unique_id': sharedUniqueId})
            });
            setTimeout(async () => {
                await _loadData();
                enqueueSnackbar(successMessage, {variant: 'success'});
            }, 500);
        } catch (error) {
            clientLog('error', error.toString(), 'shareobjecthistorypanel');
            enqueueSnackbar(_isForbidden(error) ? t('sharingNotAllowed', 'Du kan ikke endre denne delingen') : errorMessage, {variant: 'error'});
        }
    };

    const _handleDeactivateShareClick = async (row) => {
        await _execPost(
            'expire',
            _getRowSharedUniqueId(row),
            t('sharingDeactivated', 'Deling deaktivert'),
            t('sharingFailedDeactivation', 'Det oppstod en feil ved deaktivering')
        );
    };

    const _handleRenewShareClick = async (row) => {
        setTimeout(async () => {
            await _execPost(
                'renew',
                _getRowSharedUniqueId(row),
                t('sharingRenewed', 'Deling fornyet'),
                t('sharingFailedRenewal', 'Det oppstod en feil ved fornyelse av deling')
            );
        }, 500);
    };

    const dateColHr = (colName, row) => getHrDateAndTime(row[colName]);

    const columns = useMemo(() => ([
        {accessorKey: 'sharedUniqueId', header: t('colSharingPortal', 'Delingsportal')},
        {
            accessorKey: 'createdAt',
            header: t('colShared', 'Delt'),
            accessorFn: row => dateColHr('createdAt', row)
        },
        {
            accessorKey: 'expiresAt',
            header: t('colExpires', 'Gyldig til'),
            accessorFn: row => dateColHr('expiresAt', row)
        },
        {accessorKey: 'recipientEmail', header: t('colSharedTo', 'Delt med')},
        {
            accessorKey: 'emailStatus',
            header: t('colEmailStatus', 'E-post'),
            accessorFn: row => deliveryStatusText(t, row),
        }
    ]), []); // eslint-disable-line react-hooks/exhaustive-deps

    const _isExpired = (row) => {
        const expiresAt = row.original?.expiresAt;
        if (!expiresAt) return false;
        return (Date.now() - new Date(expiresAt).getTime()) > 3000;
    };

    const translations = useMemo(() => getTableTranslations(t), [t]);

    const table = useMaterialReactTable({
        localization: translations,
        data,
        columns,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enablePagination: false,
        enableTopToolbar: false,
        enableBottomToolbar: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableGlobalFilter: false,
        enableRowActions: true,
        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: t('colActions', 'Handlinger'),
                Header: () => <span style={visuallyHidden}>{t('colActions', 'Handlinger')}</span>,
            },
        },
        renderRowActions: ({row}) => (
            <Stack direction="row" spacing={1} sx={{justifyContent: "center", alignItems: "center"}}>
                {_canChange(row) && (
                    <>
                        <Tooltip title={t('toltipDeactivateSharing', 'Deaktiver deling')}>
                            <IconButton color="error" onClick={() => _handleDeactivateShareClick(row)}>
                                <CancelIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('tooltipRenewSharing', 'Utvid delingsperioden')}>
                            <IconButton color="success" onClick={() => _handleRenewShareClick(row)}>
                                <EventRepeatIcon/>
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <Tooltip title={row.original['sharedUniqueId']}>
                    <Link
                        aria-label={t('openInSharingPortal', 'Åpne i delingsportal')}
                        target="_blank"
                        to={`${window._env_.REACT_APP_SHARE_PORTAL_URL}/${row.original['sharedUniqueId']}`}
                        style={{color: 'inherit', marginTop: '4px'}}
                    >
                        <OpenInNewIcon/>
                    </Link>
                </Tooltip>
            </Stack>
        ),
        initialState: {columnVisibility: {sharedUniqueId: false}},
        muiTableBodyRowProps: ({row}) => ({
            sx: _isExpired(row) ? {
                opacity: 0.5,
                textDecoration: 'line-through',
                fontStyle: 'italic',
            } : {}
        }),
    });

    const _loadData = useCallback(async () => {
        const uniqueId = model?.uniqueId;
        if (!uniqueId) return;
        const res = await damsFetch(`/share/history/file/${uniqueId}`, {
            headers: {'correlation-id': crypto.randomUUID()}
        });
        setData(res?.history ?? []);
    }, [model?.uniqueId]);

    useEffect(() => {
        if (!open) {
            setData([]);
            return;
        }
        if (!model?.uniqueId) return;

        let isCancelled = false;
        (async () => {
            const res = await damsFetch(`/share/history/file/${model.uniqueId}`, {
                headers: {'correlation-id': crypto.randomUUID()}
            });
            if (!isCancelled) setData(res?.history ?? []);
        })();
        return () => {
            isCancelled = true;
        };
    }, [open, model?.uniqueId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return <MaterialReactTable table={table}/>;
};

